import { Fragment, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import { Collapse, ListItemButton, ListItemText, Menu } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ExpandLess, ExpandMore, UnfoldMore } from "@mui/icons-material";
import { useAuth } from "../../services/auth/AuthContext";


const PageList = ({
  pages,
  selectedPage,
  handlePageChange,
}) => {
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  const userRoles = authUser.role;

  const topLevelPages = pages.filter(page => !page.parent);
  const [currExpandedItem, setCurrExpandedItem] = useState(selectedPage.parent ?? null);

  const isAllowed = (page) => page.allowedRoles && (
    page.allowedRoles.includes('*') ||
    page.allowedRoles.some(value => userRoles.includes(value))
  );

  return (
    <List component={'div'}>
      {topLevelPages.map((page) => isAllowed(page) && (
        <Fragment key={page.value}>
          {page.children?.length > 0 ? (
            <ListItemButton
              onClick={() => setCurrExpandedItem((prev) => prev !== page.value ? page.value : null)}
            >
              <ListItemText primary={t(page.label)} />
              {currExpandedItem === page.value ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          ) : (
            <ListItemButton
              selected={selectedPage.value === page.value}
              onClick={() => handlePageChange(page)}
            >
              <ListItemText primary={t(page.label)} />
            </ListItemButton>
          )}

          {page.children && (
            <Collapse
              in={currExpandedItem === page.value}
              timeout="auto"
              unmountOnExit
            >
              <List
                component="div"
                disablePadding
                sx={{ pl: 4 }}
              >
                {page.children.map((childPageValue) => {
                  const childPage = pages.find(p => p.value === childPageValue);
                  return isAllowed(childPage) && (
                    <ListItemButton
                      key={childPage.value}
                      selected={selectedPage.value === childPage.value}
                      onClick={() => handlePageChange(childPage)}
                    >
                      <ListItemText primary={t(childPage.label)} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          )}
        </Fragment>
      ))}
    </List>
  );
};

export default function SideBar({
  pages,
  selectedPage: selectedPageValue,
  setSelection,
  footer = null,
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const { t } = useTranslation();

  const [menuAnchorElem, setMenuAnchorElem] = useState(null);
  const selectedPage = pages.find(page => page.value === selectedPageValue);

  return (
    <Box width={isSmallScreen ? '100%' : 'auto'}>
      {/* Sidebar for large screens */}
      {!isSmallScreen && (
        <Drawer
          sx={{
            width: "100%", // Full width of parent container
            flexShrink: 0,
            height: "100%", // Relative height to the parent component
            "& .MuiDrawer-paper": {
              width: "100%", // Full width of parent container
              height: "100%", // Relative height
              boxSizing: "border-box",
              position: "relative",
              border: 'none',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <PageList
            key={selectedPageValue}
            pages={pages}
            selectedPage={selectedPage}
            handlePageChange={setSelection}
          />
          {footer && <Box px={2} pb={2}>{footer}</Box>}
        </Drawer>
      )}

      {isSmallScreen ? (
        <AppBar position="sticky">
          <Box
            onClick={(e) => setMenuAnchorElem(e.currentTarget)}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
            sx={(theme) => ({
              padding: theme.spacing(0.8, 1.5),
              background: theme.palette.grey[100],
              color: theme.palette.text.primary,
              borderRadius: 1,
              fontSize: '1rem',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: theme.palette.divider,
            })}
          >
            {t(selectedPage.label)}
            <UnfoldMore fontSize="small" />
          </Box>

          <Menu
            open={!!menuAnchorElem}
            anchorEl={menuAnchorElem}
            onClose={() => setMenuAnchorElem(null)}
            sx={{
              '& .MuiMenu-list': {
                width: '420px',
                maxWidth: 'calc(100vw - 2rem)'
              }
            }}
          >
            <PageList
              key={selectedPageValue}
              pages={pages}
              selectedPage={selectedPage}
              handlePageChange={setSelection}
            />
            {footer && <Box px={2} pb={2}>{footer}</Box>}
          </Menu>
        </AppBar>
      ) : null}
    </Box>
  );
}
